import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { penaltyAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Undo2 } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function PenaltyRevertDialog({ penalty, onOpenChange, onReverted }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const ded = penalty?.deduction || {};

  const handleRevert = async () => {
    setSaving(true);
    try {
      const res = await penaltyAPI.revertDeduction(penalty.id, reason.trim());
      const sr = res.data.salary_record;
      if (sr) {
        toast.success(`Deduction reverted — bank transfer restored to ${fmt(sr.bank_transfer)}`);
      } else {
        toast.success('Deduction reverted');
      }
      onReverted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert deduction');
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!penalty} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="penalty-revert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="h-5 w-5 text-amber-600" />Revert Salary Deduction
          </DialogTitle>
          <DialogDescription>
            {ded.type === 'incentive'
              ? `The incentive deduction record (${fmt(ded.amount)} — ${ded.month}/${ded.year}) for ${penalty?.employee_name} will be removed. The penalty returns to Pending.`
              : `${fmt(ded.amount)} will be removed from ${penalty?.employee_name}'s Recovery Amount for ${ded.month}/${ded.year}, and the Bank Transfer will be restored. The penalty returns to Pending.`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-1">
          <Label className="text-xs">Reason for reverting *</Label>
          <Textarea
            className="mt-1 min-h-[60px]"
            rows={2}
            placeholder="e.g. deducted to the wrong month"
            value={reason}
            onChange={e => setReason(e.target.value)}
            data-testid="revert-reason-input"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            onClick={handleRevert}
            disabled={saving || !reason.trim()}
            data-testid="revert-confirm-btn"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Revert Deduction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
